import { translate } from "app/language/service"
import {
  GEO_TARGETS_TYPES,
  IForm,
  IFormCoordinate,
  IFormGeoTargets,
  IGeoTargetsListItem,
  IGeoTargetsSelection
} from "form/model"
import { getCitiesWithoutMetropolitanCities } from "shape/tools/metropolitan"
import {
  IGeoShapeCity,
  IGeoShapeCountry,
  IGeoShapeMetropolitan,
  IGeoShapePostalCode,
  IGeoShapeState
} from "../../shape/model"

export const getCoordinateItemId = (id: number): string => `coordinate|${id}`

export const getCityItemId = (id: string): string => `city|${id}`

export const getPostalCodeItemId = (id: string): string => `postal-code|${id}`

export const getStateItemId = (id: string): string => `state|${id}`

export const getCountryItemId = (id: string): string => `country|${id}`

export const getMetropolitanItemId = (id: string): string => `metropolitan|${id}`

export const getGeoTargetsCount = ({ geoTargets }: IForm) =>
  geoTargets.coordinates.length +
  getCitiesWithoutMetropolitanCities(geoTargets.cities, geoTargets.metropolitans).length +
  geoTargets.countries.length +
  geoTargets.postalCodes.length +
  geoTargets.states.length +
  geoTargets.metropolitans.length

export const getSelectionFromItemIds = (itemIds: string[]): IGeoTargetsSelection => {
  const shapeSelection: IGeoTargetsSelection = {
    cityIds: [],
    postalCodeIds: [],
    stateIds: [],
    countryIds: [],
    coordinateIds: [],
    metropolitanIds: []
  }
  for (const itemId of itemIds) {
    const [type, id] = itemId.split("|")
    if (type === "coordinate") {
      shapeSelection.coordinateIds.push(Number(id))
    } else if (type === "city") {
      shapeSelection.cityIds.push(id)
    } else if (type === "postal-code") {
      shapeSelection.postalCodeIds.push(id)
    } else if (type === "state") {
      shapeSelection.stateIds.push(id)
    } else if (type === "country") {
      shapeSelection.countryIds.push(id)
    } else if (type === "metropolitan") {
      shapeSelection.metropolitanIds.push(id)
    } else {
      throw Error(`Type "${type}" not supported`)
    }
  }
  return shapeSelection
}

export const getItemIdsFromSelection = (shapeSelection: IGeoTargetsSelection): string[] => [
  ...shapeSelection.coordinateIds.map(c => getCoordinateItemId(c)),
  ...shapeSelection.cityIds.map(c => getCityItemId(c)),
  ...shapeSelection.postalCodeIds.map(p => getPostalCodeItemId(p)),
  ...shapeSelection.stateIds.map(s => getStateItemId(s)),
  ...shapeSelection.countryIds.map(c => getCountryItemId(c)),
  ...shapeSelection.metropolitanIds.map(m => getMetropolitanItemId(m))
]

export function getGeoTargetListItems(
  { coordinates, cities, postalCodes, states, countries, metropolitans }: IFormGeoTargets,
  typeFilter?: GEO_TARGETS_TYPES[]
): IGeoTargetsListItem[] {
  if (!typeFilter || typeFilter.length === 0) {
    return [
      ...getCoordinateItems(coordinates),
      ...getCitiesItems(getCitiesWithoutMetropolitanCities(cities, metropolitans)),
      ...getPostalCodeItems(postalCodes),
      ...getStateItems(states),
      ...getCountryItems(countries),
      ...getMetropolitanItems(metropolitans)
    ]
  }
  const items: IGeoTargetsListItem[] = []
  if (typeFilter.includes(GEO_TARGETS_TYPES.COORDINATE)) {
    items.push(...getCoordinateItems(coordinates))
  }
  if (typeFilter.includes(GEO_TARGETS_TYPES.CITY)) {
    items.push(...getCitiesItems(cities))
  }
  if (typeFilter.includes(GEO_TARGETS_TYPES.POSTAL_CODE)) {
    items.push(...getPostalCodeItems(postalCodes))
  }
  if (typeFilter.includes(GEO_TARGETS_TYPES.STATE)) {
    items.push(...getStateItems(states))
  }
  if (typeFilter.includes(GEO_TARGETS_TYPES.COUNTRY)) {
    items.push(...getCountryItems(countries))
  }
  if (typeFilter.includes(GEO_TARGETS_TYPES.METROPOLITAN)) {
    items.push(...getMetropolitanItems(metropolitans))
  }
  return items
}

export function getGeoTargetTypeTranslation(type: GEO_TARGETS_TYPES): string {
  return translate(
    type === GEO_TARGETS_TYPES.COORDINATE
      ? "campaign.geo-targets.tab.coordinate.title"
      : `campaign.geo-targets.shape.${type}.title`
  )
}

export function getGeoTargetTypeColor(targetType: GEO_TARGETS_TYPES): string {
  switch (targetType) {
    case GEO_TARGETS_TYPES.COORDINATE: {
      return "#ffd6ba"
    }
    case GEO_TARGETS_TYPES.POSTAL_CODE: {
      return "#f6f1d1"
    }
    case GEO_TARGETS_TYPES.CITY: {
      return "#89b0ae"
    }
    case GEO_TARGETS_TYPES.STATE: {
      return "#cfd7c7"
    }
    case GEO_TARGETS_TYPES.COUNTRY: {
      return "#a7c6da"
    }
    case GEO_TARGETS_TYPES.METROPOLITAN: {
      return "#ecd5fd"
    }
  }
}

function getCoordinateItems(coordinates: IFormCoordinate[]): IGeoTargetsListItem[] {
  return coordinates.map(c => ({
    id: getCoordinateItemId(c.id),
    latitude: c.latitude,
    longitude: c.longitude,
    name: c.name || "",
    radius: c.radius,
    type: GEO_TARGETS_TYPES.COORDINATE,
    coordinate: c
  }))
}

function getCitiesItems(cities: IGeoShapeCity[]): IGeoTargetsListItem[] {
  return cities.map(c => ({
    id: getCityItemId(c.external_id),
    latitude: null,
    longitude: null,
    name: c.name,
    radius: null,
    type: GEO_TARGETS_TYPES.CITY,
    shape: c,
    country_id: c.country_id,
    state_id: c.state_id
  }))
}

function getPostalCodeItems(postalCodes: IGeoShapePostalCode[]): IGeoTargetsListItem[] {
  return postalCodes.map(p => ({
    id: getPostalCodeItemId(p.postal_code),
    latitude: null,
    longitude: null,
    name: p.postal_code,
    radius: null,
    type: GEO_TARGETS_TYPES.POSTAL_CODE,
    shape: p,
    country_id: p.country_id,
    state_id: p.state_id
  }))
}

function getStateItems(states: IGeoShapeState[]): IGeoTargetsListItem[] {
  return states.map(s => ({
    id: getStateItemId(s.state_shape_id),
    latitude: null,
    longitude: null,
    name: s.name,
    radius: null,
    type: GEO_TARGETS_TYPES.STATE,
    shape: s,
    country_id: s.country_id
  }))
}

function getCountryItems(countries: IGeoShapeCountry[]): IGeoTargetsListItem[] {
  return countries.map(c => ({
    id: getCountryItemId(c.country_shape_id),
    latitude: null,
    longitude: null,
    name: c.name,
    radius: null,
    type: GEO_TARGETS_TYPES.COUNTRY,
    shape: c
  }))
}

function getMetropolitanItems(metropolitans: IGeoShapeMetropolitan[]): IGeoTargetsListItem[] {
  return metropolitans.map(m => ({
    id: getMetropolitanItemId(m.shape_id),
    latitude: null,
    longitude: null,
    name: translate(m.labelKey, m.cityIds.length),
    radius: null,
    type: GEO_TARGETS_TYPES.METROPOLITAN,
    shape: m
  }))
}
